// Read-only access context for learn, plus the superadmin "view as read-only"
// preview.
//
// `auth.whoami` reports `readOnly: true` for staff admitted via
// `learnAccess: "read"` (e.g. Alex, Head of Growth) — they see every cohort a PL
// sees but hold no write role. LayoutShell resolves the flag once and publishes
// it here so the write surfaces (attendance checkboxes, notes editors, the
// add-attendee dialog, the roster notes cell) can render as static text.
//
// PREVIEW: a superadmin (Lisa, Tara) can flip `previewing` to render the app
// exactly as a viewer sees it, without a second account. This is a faithful
// preview rather than an approximation because the read queries return an
// IDENTICAL payload to everyone who passes the gate — `getProgramView` masks
// nothing by role, so which controls render is the only thing that differs.
//
// What the preview does NOT do is change the previewer's own server-side
// permissions: a superadmin stays a superadmin, so a write control that leaked
// past `readOnly` would still succeed if clicked. That is exactly why the
// preview keeps a persistent banner on screen instead of silently pretending to
// be someone else — the banner is the standing reminder that a click here is a
// real write.
//
// None of this is a security boundary. Every learn mutation is gated server-side
// on requireProgramLeader / requireProgramLeaderOrOps, so a real viewer who
// bypasses the UI still gets FORBIDDEN.
import { createContext, useCallback, useContext, useMemo, useState } from "react";

// Session-scoped, not localStorage: previewing is a thing you do for a minute to
// check a layout, not a preference that should outlive the tab and quietly
// convince you next week that your own controls have vanished.
const PREVIEW_KEY = "learn:preview-read-only";

interface LearnAccessValue {
  /**
   * The EFFECTIVE read-only flag — true when the account genuinely holds
   * `learnAccess: "read"`, or when a superadmin is previewing. This is what
   * every write surface consults.
   */
  readOnly: boolean;
  /** What the server actually said, ignoring any preview. */
  actualReadOnly: boolean;
  /** True when this account may toggle the preview (superadmin only). */
  canPreview: boolean;
  /** True while the preview is active. */
  previewing: boolean;
  setPreviewing: (next: boolean) => void;
}

const LearnAccessContext = createContext<LearnAccessValue>({
  readOnly: false,
  actualReadOnly: false,
  canPreview: false,
  previewing: false,
  setPreviewing: () => {},
});

function readStoredPreview(): boolean {
  // Wrapped: Safari private mode throws on sessionStorage access rather than
  // returning null, and a preview toggle is not worth a blank app.
  try {
    return window.sessionStorage.getItem(PREVIEW_KEY) === "1";
  } catch {
    return false;
  }
}

export function LearnAccessProvider({
  readOnly,
  canPreview,
  children,
}: {
  readOnly: boolean;
  canPreview: boolean;
  children: React.ReactNode;
}) {
  const [previewing, setPreviewingState] = useState(readStoredPreview);

  const setPreviewing = useCallback((next: boolean) => {
    setPreviewingState(next);
    try {
      if (next) window.sessionStorage.setItem(PREVIEW_KEY, "1");
      else window.sessionStorage.removeItem(PREVIEW_KEY);
    } catch {
      // Storage unavailable — the toggle still works for this render tree, it
      // just won't survive a reload. Not worth surfacing.
    }
  }, []);

  // `canPreview &&` on both: a stale sessionStorage flag from an earlier session
  // must not put a non-superadmin into a preview they cannot exit, and must not
  // hide a real PL's attendance checkboxes mid-delivery.
  const effectivePreviewing = canPreview && previewing;

  const value = useMemo<LearnAccessValue>(
    () => ({
      readOnly: readOnly || effectivePreviewing,
      actualReadOnly: readOnly,
      canPreview,
      previewing: effectivePreviewing,
      setPreviewing,
    }),
    [readOnly, canPreview, effectivePreviewing, setPreviewing]
  );

  return (
    <LearnAccessContext.Provider value={value}>
      {children}
    </LearnAccessContext.Provider>
  );
}

/**
 * `true` when the signed-in staff member may read learn but not write to it —
 * either genuinely, or because a superadmin is previewing that experience.
 *
 * Defaults to `false` outside a provider: a component rendered without the
 * provider is a bug in this app, and failing open on presentation is correct
 * when the server is the actual gate — failing closed would hide a real PL's
 * attendance checkboxes and silently break delivery.
 */
export function useReadOnly(): boolean {
  return useContext(LearnAccessContext).readOnly;
}

/** Full access state — for the header controls that drive the preview. */
export function useLearnAccess(): LearnAccessValue {
  return useContext(LearnAccessContext);
}
